import {
  Box,
  Stack
} from "@mui/material";
import { uploadData } from "aws-amplify/storage";
import { format } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import AbsForm from "../../abstract/Form";
import CardWithHeader from "../../components/cards/CardWithHeader";
import FileUploadButton from "../../components/inputs/FileUploadButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { FormProvider } from "../../contexts/FormContext";
import { colors } from "../../theme";
import { getOocyteImagePath } from "../CustomizeReport/types";
import { config } from "./config";
import OocyteCard from "./OocyteCard";
import {
  createSchema,
  FormData,
  FormResponse,
  imagesSchema,
  OocyteCondition,
  OocyteUpload,
} from "./types";

const maximumOocyteUploads = 16;
const minUploadWidth = 500;
const minUploadHeight = 500;

interface OocyteQualityAssessmentFormProps {}

type NullableFormData = {
  [K in keyof FormData]?: FormData[K] | null;
};

const OocyteQualityAssessmentForm: React.FC<
  OocyteQualityAssessmentFormProps
> = (props) => {
  const [oocyteUploads, setOocyteUploads] = useState<OocyteUpload[]>([]);
  const { translate } = useApp();
  const translations = useMemo(() => translate("oocyte"), [translate]);

  const conditionOptions = Object.keys(OocyteCondition).map((value) => ({
    value: value,
    label: translations.form.labels[value],
  }));

  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
  const [filesMap, setFilesMap] = useState<Map<string, File>>(new Map());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorIndex, setAnanchorIndex] = useState<number | null>(null);
  const [imagesError, setImagesError] = useState<string[] | null>(null);
  const [mainImagesError, setMainImagesError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NullableFormData>({
    name: null,
    birth: null,
    ethnicity: [],
    height: null,
    weight: null,
  });

  const { updateBreadcrumbs } = useApp();
  const { showError } = useError();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { apiService, setSubmitting } = useApi();
  const fileUploadButtonRef = useRef<HTMLButtonElement>(null);
  const [validateOnChange, setValidateOnChange] = useState(false);

  console.info("user", user);

  useEffect(() => {
    console.info("oocyteUploads changed", oocyteUploads);
  }, [oocyteUploads]);

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.oocyteAssessment,
        onClick: () => {
          navigate("/oocyte");
        },
      },
      {
        label: translations.breadcrumbs.newCycle,
      },
    ]);

    sendPreflightRequest();
  }, []);

  const sendPreflightRequest = () => {
    apiService.head("/oocyte").then((response) => {
      console.info("preflight response", response);
    });
  };

  const handleUploadReplace = (index: number) => {
    setReplacingIndex(index);
    fileUploadButtonRef.current?.click();
  };

  const handleUploadDelete = (index: number) => {
    const oocyteUpload = oocyteUploads[index!];
    const oocyteUploadsCopy = [...oocyteUploads];
    const filesMapCopy = new Map(filesMap);
    filesMapCopy.delete(oocyteUpload.fileUrl);
    setFilesMap(filesMapCopy);
    oocyteUploadsCopy.splice(index!, 1);
    setOocyteUploads(oocyteUploadsCopy);
  };

  const renderUploadedOocytes = useMemo(() => {
    if (oocyteUploads.length === 0) return null;

    return (
      <Stack spacing={1}>
        {oocyteUploads.map((oocyteUpload, index) => (
          <OocyteCard
            key={index}
            oocyteUpload={oocyteUpload}
            index={index}
            translations={translations}
            handleUploadReplace={handleUploadReplace}
            handleUploadDelete={handleUploadDelete}
            setOocyteUploads={setOocyteUploads}
            oocyteUploads={oocyteUploads}
            conditionOptions={conditionOptions}
          />
        ))}
      </Stack>
    );
  }, [oocyteUploads, imagesError]);

  const updateCycleImages = (files: File[]) => {
    if (files === null) {
      return false;
    }
    if (oocyteUploads.length + files.length > maximumOocyteUploads) {
      showError(
        translate("oocyte", "form.errors.maxImagesExceeded", {
          maximum: `${maximumOocyteUploads}`,
        })
      );
      return false;
    }
    const filesMapCopy = new Map(filesMap);
    const oocyteUploadsCopy = [...oocyteUploads];

    if (replacingIndex !== null) {
      const oocyteUpload = oocyteUploads[replacingIndex];
      const file = files[0];
      const url = URL.createObjectURL(file);
      filesMapCopy.set(url, file);
      oocyteUploadsCopy[replacingIndex] = {
        ...oocyteUpload,
        fileUrl: url,
        originalName: file.name,
      };
      setOocyteUploads(oocyteUploadsCopy);
      setFilesMap(filesMapCopy);
      setReplacingIndex(null);
      return;
    }

    files.forEach((file) => {
      const url = URL.createObjectURL(file);
      filesMapCopy.set(url, file);
      oocyteUploadsCopy.push({
        fileUrl: url,
        condition: null,
        hph: null,
        originalName: file.name,
        status: "init",
      });
    });
    setOocyteUploads(oocyteUploadsCopy);
    setFilesMap(filesMapCopy);

    console.info("filesMap", filesMap);
  };

  const getFormData = (formData: any, config: any) => {
    const ret = {
      ...formData,
      patient: formData?.name,
      birth: formData?.birth ? format(formData?.birth, "yyyy-MM-dd") : null,
      ethnicity: formData?.ethnicity.map((e: string) => Number(e)),
      files: oocyteUploads.length,
      afc: {
        ...(formData?.["afc.date"] && {
          date: format(formData["afc.date"], "yyyy-MM-dd"),
        }),
        ...(formData?.["afc.left"] && {
          left: parseFloat(formData["afc.left"]),
        }),
        ...(formData?.["afc.right"] && {
          right: parseFloat(formData["afc.right"]),
        }),
      },
      amh: {
        ...(formData?.["amh.date"] && {
          date: format(formData["amh.date"], "yyyy-MM-dd"),
        }),
        ...(formData?.["amh.value"] && {
          value: parseFloat(formData["amh.value"]),
        }),
      },
      fsh: {
        ...(formData?.["fsh.date"] && {
          date: format(formData["fsh.date"], "yyyy-MM-dd"),
        }),
        ...(formData?.["fsh.value"] && {
          value: parseFloat(formData["fsh.value"]),
        }),
      },
      retrieval: {
        ...(formData?.["retrieval.date"] && {
          date: format(formData["retrieval.date"], "yyyy-MM-dd"),
        }),
        ...(formData?.["retrieval.count"] && {
          count: parseInt(formData["retrieval.count"]),
        }),
      },
    };
    return ret;
  };

  const uploadOocytes = async (oocyteId: string) => {
    console.info("identityId", session?.identityId);

    for (let index = 0; index < oocyteUploads.length; index++) {
      try {
        const oocyteUpload = oocyteUploads[index];
        const imagePath = getOocyteImagePath(user.clinic, oocyteId, index + 1, oocyteUpload.originalName);
        const meta: any = {
          filename: oocyteUpload.originalName,
        };
        if (oocyteUpload.hph) {
          meta.hph = oocyteUpload.hph.toFixed(2);
        }
        if (oocyteUpload.condition) {
          meta.frozen =
            oocyteUpload.condition === OocyteCondition.frozen ? "1" : "0";
        }
        await uploadData({
          data: filesMap.get(oocyteUpload.fileUrl)!,
          path: imagePath,
          options: {
            metadata: meta,
          },
        });

        oocyteUploads[index].path = imagePath;
        oocyteUploads[index].status = "uploaded";
      } catch (error) {
        console.error("Error uploading oocyte", error);
        oocyteUploads[index].status = "error";
        showError(translations.form.messages.errorUploadingImages);
      }
    }
  };

  useEffect(() => {
    if (validateOnChange) {
      validateImages();
    }
  }, [oocyteUploads]);

  const validateImages = useCallback(() => {
    console.info("oocyteUploads", oocyteUploads);

    const result = imagesSchema.safeParse(oocyteUploads);
    console.info("result", result);

    if (!result.success) {
      const errorDetails = result.error.flatten();
      console.info("errorDetails", errorDetails);

      if (errorDetails.formErrors.length) {
        setMainImagesError(translate("oocyte", errorDetails.formErrors[0]));
      }
      if (errorDetails.fieldErrors?.[0]) {
        setImagesError(
          errorDetails.fieldErrors[0].map((e) => translate("oocyte", e))
        );
      }

      return false;
    }
    setImagesError(null);
    setMainImagesError(null);
    return true;
  }, [oocyteUploads]);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);
    if (!validateImages()) {
      return false;
    }

    try {
      setSubmitting(true);
      const response = await apiService.put<FormResponse, FormData>(
        "/oocyte",
        getFormData(formData, config) as any
      );
      console.log("API Response:", response);

      await uploadOocytes(response.cycle.id);

      navigate(`/oocyte`, { state: { newId: response.cycle.id } });
    } catch (error) {
      console.error("Error submitting form", error);
      showError(translations.form.messages.errorSubmittingForm);
    } finally {
      setSubmitting(false);
    }
  }, [oocyteUploads, filesMap, formData]);

  const handleDataChange = (data: any) => {
    console.info("data", data);
    setFormData(data);
  };

  return (
    <FormProvider>
      <Stack gap={3} alignItems={"flex-start"} direction={"row"}>
        <Box width={"60%"}>
          <AbsForm
            langCategory="oocyte"
            data={formData}
            system={user.config.weightHeightUnit ?? "metric"}
            initialValues={{}}
            config={config}
            onSubmit={handleSubmit}
            schema={createSchema(user.config.weightHeightUnit ?? "metric")}
            onChange={handleDataChange}
            submitText={translations.form.form.submit}
            uploads={oocyteUploads}
          />
        </Box>
        <Box width={"40%"}>
          <CardWithHeader
            headerColor={"white"}
            title={translations.form.card.title}
          >
            <Stack gap={2}>
              {mainImagesError && (
                <Box color={colors.red[600]}>{mainImagesError}</Box>
              )}
              <FileUploadButton
                caption={translations.form.upload.caption}
                title={translations.form.upload.title}
                accept="image/*"
                ref={fileUploadButtonRef as React.RefObject<HTMLButtonElement>}
                keepShowingUploadIcon={true}
                onChange={updateCycleImages}
                onError={(file, error) => {
                  showError(
                    translate("general", error, {
                      file: file.name,
                      width: `${minUploadWidth}`,
                      height: `${minUploadHeight}`,
                    })
                  );
                }}
              />
              {renderUploadedOocytes}
            </Stack>
          </CardWithHeader>
        </Box>
      </Stack>
    </FormProvider>
  );
};

export default OocyteQualityAssessmentForm;
